import type {
  FiWeeklySummarySettingsCore,
  FiWeeklySummarySettingsFocusArea,
  FiWeeklySummarySettingsTone,
} from "@mapmycustomers/shared/types/entity/fieldInsight";

import { Tone } from "../enums";
import type { AnalysisConfig } from "../types";

import { DEFAULT_ANALYSIS_CONFIG } from "./consts";
import { datasetFromApi } from "./dataset";

// Keyed on the wire union, not string: a tone the API adds is then a build error here rather
// than an undefined that silently falls back to the default tone.
const BE_TONE_TO_FE: Record<FiWeeklySummarySettingsTone, Tone> = {
  brief: Tone.BRIEF,
  casual: Tone.CASUAL,
  formal: Tone.FORMAL,
};

// One converter for every read, because a personal row, an org preset and the resolved read all
// carry the same settings core. Two copies accepted each other's input, so a call routed to the
// wrong one returned a quietly different result with nothing to catch it.
export const fromBeSettingsCore = (
  settings: FiWeeklySummarySettingsCore | null | undefined
): AnalysisConfig => {
  if (!settings) {
    return DEFAULT_ANALYSIS_CONFIG;
  }
  const saved = settings.focusAreas;
  return {
    availableFocusAreas: saved?.map(({ opt }) => opt) ?? [],
    customInstructions: settings.customInstructions ?? DEFAULT_ANALYSIS_CONFIG.customInstructions,
    dataset: datasetFromApi(settings.dataset),
    focusAreas: saved?.filter(({ isVisible }) => isVisible).map(({ opt }) => opt) ?? [],
    tone:
      (settings.tone ? BE_TONE_TO_FE[settings.tone] : undefined) ?? DEFAULT_ANALYSIS_CONFIG.tone,
  };
};

// The API rejects a focus-area set that is not exactly the org's own, so the payload is built
// from the keys it handed us rather than any list of our own.
export const toFocusAreaPayload = (config: AnalysisConfig): FiWeeklySummarySettingsFocusArea[] => {
  const visible = new Set(config.focusAreas);
  return config.availableFocusAreas.map((key) => ({ isVisible: visible.has(key), opt: key }));
};
